// platform.doc — a single named JSON blob.
//
// For app config, per-user profiles, anything that lives at one
// well-known path. Path supports `{token}` interpolation against
// `me`, so `app.doc("users/{sub}/profile")` resolves to the current
// user's profile path without further wiring.

import {
  DeleteObjectCommand,
  GetObjectCommand,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";

import type { Me } from "./me.ts";

export interface DocConfig {
  /** Override the content type for non-JSON payloads (e.g.
   *  `"application/octet-stream"` to store raw bytes). Default
   *  `"application/json"` decodes on `get` and JSON-stringifies on `put`. */
  contentType?: string;
}

export interface DocHandle<T> {
  /** Fully-resolved storage path (token interpolation already applied). */
  readonly path: string;
  /** Get the current value. Returns null if missing. */
  get(): Promise<T | null>;
  /** Replace the value. */
  put(value: T): Promise<void>;
  /** Remove it. */
  delete(): Promise<void>;
}

export interface DocDeps {
  getClient: () => Promise<S3Client>;
  bucket: string;
  /** Path under the bucket where docs live. Slash-terminated. */
  /** Slash-terminated base prefix, or a FUNCTION when it depends on the
   *  session (`scope: "private"`), resolved per operation — see collection.ts. */
  basePath: string | (() => string);
  /** Resolve `{token}` interpolation against the current session. */
  me: () => Me | null;
}

const TOKEN = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

export function createDoc<T = unknown>(
  deps: DocDeps,
  name: string,
  config: DocConfig = {},
): DocHandle<T> {
  const contentType = config.contentType ?? "application/json";
  const base = () => (typeof deps.basePath === "function" ? deps.basePath() : deps.basePath);
  /** Resolved per call: for `scope: "private"` the prefix depends on the
   *  session, which may not exist when the handle is created. */
  const pathOf = () => `${base()}${resolveTokens(name, deps.me)}`;
  const isJson = contentType === "application/json";

  return {
    // A getter, so `doc.path` still reads as a string but resolves on access.
    get path() {
      return pathOf();
    },
    async get(): Promise<T | null> {
      const s3 = await deps.getClient();
      try {
        const res = await s3.send(new GetObjectCommand({ Bucket: deps.bucket, Key: pathOf() }));
        const bytes = await readBytes(res.Body);
        if (isJson) return JSON.parse(new TextDecoder().decode(bytes)) as T;
        return bytes as unknown as T;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    async put(value: T): Promise<void> {
      const s3 = await deps.getClient();
      const body = isJson
        ? JSON.stringify(value, null, 2)
        : (value as unknown as Uint8Array | string);
      await s3.send(
        new PutObjectCommand({
          Bucket: deps.bucket,
          Key: pathOf(),
          Body: body,
          ContentType: contentType,
        }),
      );
    },
    async delete(): Promise<void> {
      const s3 = await deps.getClient();
      await s3.send(new DeleteObjectCommand({ Bucket: deps.bucket, Key: pathOf() }));
    },
  };
}

function resolveTokens(name: string, getMe: () => Me | null): string {
  return name.replace(TOKEN, (_full, token) => {
    const me = getMe();
    if (!me) {
      throw new Error(
        `platform.doc("${name}"): {${token}} interpolation requires an authenticated session`,
      );
    }
    const v = (me as unknown as Record<string, unknown>)[token];
    if (typeof v !== "string") {
      throw new Error(
        `platform.doc("${name}"): {${token}} resolves to ${typeof v}, expected string`,
      );
    }
    return v;
  });
}

async function readBytes(body: unknown): Promise<Uint8Array> {
  return (body as { transformToByteArray: () => Promise<Uint8Array> }).transformToByteArray();
}

function is404(e: unknown): boolean {
  const code = (e as { name?: string; Code?: string })?.name;
  const status = (e as { $metadata?: { httpStatusCode?: number } })?.$metadata?.httpStatusCode;
  return status === 404 || code === "NoSuchKey" || code === "NotFound";
}
